import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  AdminCreateUserRequest,
  AdminPatchUserRequest,
  CreateWorkshopRequest,
  ResetChefPasswordRequest,
  UpdateWorkshopRequest,
} from '@wf/shared'
import { api } from '../api/client'

// Betreiber-/Super-Admin: App-Verwaltung. Daten-Hooks für die /admin/*-Routen (nur is_superadmin).
const LIST_KEY = ['admin', 'workshops'] as const
const STATS_KEY = ['admin', 'stats'] as const
const detailKey = (id: number) => ['admin', 'workshop', id] as const

export function useAdminWorkshops() {
  return useQuery({ queryKey: LIST_KEY, queryFn: api.adminListWorkshops })
}

/** SaaS-KPIs (MRR/ARPA/Lizenzen/Aktivität) fürs Betreiber-Dashboard. */
export function useAdminStats() {
  return useQuery({ queryKey: STATS_KEY, queryFn: api.adminStats })
}

export function useAdminWorkshop(id: number) {
  return useQuery({ queryKey: detailKey(id), queryFn: () => api.adminGetWorkshop(id) })
}

export function useCreateWorkshop() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateWorkshopRequest) => api.adminCreateWorkshop(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LIST_KEY })
      void qc.invalidateQueries({ queryKey: STATS_KEY })
    },
  })
}

export function useUpdateWorkshop() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: UpdateWorkshopRequest }) => api.adminUpdateWorkshop(id, body),
    onSuccess: (_res, { id }) => {
      void qc.invalidateQueries({ queryKey: LIST_KEY })
      void qc.invalidateQueries({ queryKey: detailKey(id) })
      void qc.invalidateQueries({ queryKey: STATS_KEY }) // Tarif-/Status-Wechsel ändert MRR/Lizenz-Zähler
    },
  })
}

export function useDeleteWorkshop() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.adminDeleteWorkshop(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LIST_KEY })
      void qc.invalidateQueries({ queryKey: STATS_KEY })
    },
  })
}

export function useResetChefPassword() {
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: ResetChefPasswordRequest }) => api.adminResetChefPassword(id, body),
  })
}

// ---- Mitarbeiter einer (fremden) Werkstatt verwalten ----
function invalidateWorkshop(qc: ReturnType<typeof useQueryClient>, workshopId: number) {
  void qc.invalidateQueries({ queryKey: detailKey(workshopId) })
  void qc.invalidateQueries({ queryKey: LIST_KEY })
}

export function useAdminCreateUser(workshopId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: AdminCreateUserRequest) => api.adminCreateUser(workshopId, body),
    onSuccess: () => invalidateWorkshop(qc, workshopId),
  })
}

export function useAdminPatchUser(workshopId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, body }: { userId: number; body: AdminPatchUserRequest }) => api.adminPatchUser(userId, body),
    onSuccess: () => invalidateWorkshop(qc, workshopId),
  })
}

export function useAdminDeleteUser(workshopId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: number) => api.adminDeleteUser(userId),
    onSuccess: () => invalidateWorkshop(qc, workshopId),
  })
}
